import { ApolloProvider } from "@apollo/client";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { App } from "antd";
import { type ReactNode, StrictMode } from "react";
import { useTranslation } from "react-i18next";

import { OnlineStatusListener } from "design-system/components/OnlineStatusListener";
import { ThemeProvider } from "design-system/components/ThemeProvider";
import { NotificationsProvider } from "design-system/contexts/NotificationsContext";
import { useHideChrome } from "shell/console/hooks/useHideChrome";
import "design-system/reset.css";
import "@fontsource-variable/space-grotesk";
import "@fontsource-variable/jetbrains-mono/index.css";

import { ClientScopingProvider } from "shell/platform/contexts/ClientScopingContext";
import { FeatureFlagsProvider } from "shell/platform/contexts/FeatureFlagsContext";
import { client } from "shell/platform/lib/graphqlClient";
import { queryClient } from "shell/platform/lib/queryClient";

import { AuthGate } from "./AuthGate";

interface ShellProps {
  children: ReactNode;
}

export const Shell = ({ children }: ShellProps) => {
  const { t } = useTranslation(["common"]);

  const isThemeEnabled = !useHideChrome();

  return (
    <StrictMode>
      <AuthGate isEnabled={!import.meta.env.VITE_ACCESS_TOKEN_OVERRIDE}>
        <ThemeProvider isEnabled={isThemeEnabled}>
          <App>
            <ApolloProvider client={client}>
              <QueryClientProvider client={queryClient}>
                <NotificationsProvider>
                  <OnlineStatusListener
                    offlineMessage={t("components.onlineStatus.offlineMessage")}
                    offlineDescription={t(
                      "components.onlineStatus.offlineDescription",
                    )}
                    onlineMessage={t("components.onlineStatus.onlineMessage")}
                    onlineDescription={t(
                      "components.onlineStatus.onlineDescription",
                    )}
                  >
                    <FeatureFlagsProvider>
                      <ClientScopingProvider>{children}</ClientScopingProvider>
                    </FeatureFlagsProvider>
                  </OnlineStatusListener>
                </NotificationsProvider>
                {import.meta.env.DEV ? (
                  <ReactQueryDevtools initialIsOpen={false} />
                ) : null}
              </QueryClientProvider>
            </ApolloProvider>
          </App>
        </ThemeProvider>
      </AuthGate>
    </StrictMode>
  );
};
